import {
  computed,
  effect,
  inject,
  Injectable,
  Injector,
  signal,
} from '@angular/core';
import { Company } from '../../model/interfaces/company.interface';
import { Project } from '../../model/interfaces/project.interface';
import { SystemSettings } from '../../model/interfaces/system-settings.interface';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { LocalStorageKeys } from '../../../auth/model/enums/local-storage.keys';
import { fetchWithCsrf } from '../../utils/fetch-helper';
import { getCookie } from '../../utils/cookie';
import { AuthService } from '../../../auth/service/auth.service';
import { ENVIRONMENT } from '@env/environment';
import { Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  filter,
  firstValueFrom,
  Observable,
  throwError,
  BehaviorSubject,
} from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { LanguageDirection } from '../../model/enums/language-direction.enum';
import { Languages } from '../../model/enums/languages.enum';

@Injectable({ providedIn: 'root' })
export class SystemService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private localStorageService = inject(LocalStorageService);
  private translateService = inject(TranslateService);
  private injector = inject(Injector);

  private sidebarExpandedSubject = new BehaviorSubject<boolean>(
    this.localStorageService.getItem('sidebarExpanded') === 'true',);
  public sidebarExpanded$ = this.sidebarExpandedSubject.asObservable();

  // CRITICAL: Sidebar disabled state to prevent navigation during modal interactions
  // When dashboard modals are open, we disable the sidebar to prevent $rootScope corruption
  private sidebarDisabledSubject = new BehaviorSubject<boolean>(false);
  public sidebarDisabled$ = this.sidebarDisabledSubject.asObservable();

  toggleSidebar(): void {
    const newState = !this.sidebarExpandedSubject.getValue();
    this.setSidebarExpanded(newState);
  }

  setSidebarExpanded(expanded: boolean): void {
    this.sidebarExpandedSubject.next(expanded);
    this.localStorageService.setItem('sidebarExpanded', expanded.toString());
  }

  get isSidebarExpanded(): boolean {
    return this.sidebarExpandedSubject.getValue();
  }

  setSidebarDisabled(disabled: boolean): void {
    this.sidebarDisabledSubject.next(disabled);
  }

  COMPANY_DEFAULT_SELECTION = 'All Organizations';
  private get authService(): AuthService {
    return this.injector.get(AuthService);
  }

  private _selectChangeTrigger = false;
  private _projectInitialized = false;

  companies = signal<Company[]>([]);
  projects = signal<Project[]>([]);
  readonly isProjectsReady = computed(() => this.projects().length > 0);

  readonly _selectedCompany = signal<string>('');
  readonly _selectedProject = signal<string | null>(null);


  readonly systemReady = computed(() => {
    const permissions = this.authService.userPermissionsSignal();
    const permissionsReady = permissions && Object.keys(permissions).length > 0;
    return this.isProjectsReady() && permissionsReady;
  });

  systemSettings!: SystemSettings;

  private _initialized = false;
  private readonly _isInitialized = signal(false);
  readonly isInitialized = computed(() => this._isInitialized());
  public isInitialized$!: Observable<boolean>;

  private _companySearchTerm = signal<string>('');
  updateCompanySearch(term: string) {
    this._companySearchTerm.set(term);
  }

  constructor() {
    this.isInitialized$ = toObservable(this._isInitialized, { injector: this.injector });

    effect(() => this._selectedProject() && this.updateLanguageByProjectDirection(),);

    // Navigate to dashboards when project changes from certain pages
    effect(() => {
      const projectId = this._selectedProject();
      if (!projectId || !this._initialized) return;

      // Skip navigation on initial project load
      if (!this._projectInitialized) {
        this._projectInitialized = true;
        return;
      }

      const currentUrl = this.router.url;
      // List of routes that should navigate to dashboard when project changes
      const routesToRedirect = ['/dictionaries'];

      const shouldRedirect = routesToRedirect.some(route => currentUrl.startsWith(route));
      if (shouldRedirect) {
        this.router.navigate(['/dashboard']);
      }
    }, { allowSignalWrites: true });
  }

  readonly filteredCompaniesOptions = computed(() => {
    const term = this._companySearchTerm().toLowerCase();
    const selectedId = this._selectedCompany();

    const filtered = this.companies().filter((company) => {
      const nameMatch = company.name.toLowerCase().includes(term);

      const projectMatch = company.projects?.some((project) => {
        return project.name.toLowerCase().includes(term);
      });

      const isSelected = selectedId && company.id === selectedId;

      return nameMatch || projectMatch || isSelected;
    });

    return [
      {
        label: this.COMPANY_DEFAULT_SELECTION,
        value: this.COMPANY_DEFAULT_SELECTION,
      },
      ...filtered.map((c) => ({
        label: c.name,
        value: String(c.id),
      })),
    ];
  });

  companiesListOptions = computed(() => [
    {
      label: this.COMPANY_DEFAULT_SELECTION,
      value: this.COMPANY_DEFAULT_SELECTION,
    },
    ...this.companies().map((company) => ({
      label: company.name,
      value: String(company.id),
    })),
  ]);

  projectsListOptions = computed(() => {
    if (!this.authService.user?.isSuperuser) {
      return this.projects().map((project) => ({
        label: project.name,
        value: project.id,
        ...(project.company?.id && { data: project.company.id }),
      }));
    }

    return this.projects()
      .filter((option) => {
        if (
          !this.selectedCompany ||
          this.selectedCompany === this.COMPANY_DEFAULT_SELECTION
        )
          return true;
        return option.company?.id === this.selectedCompany;
      })
      .map((project) => ({
        label: project.name,
        value: project.id,
        ...(project.company?.id && { data: project.company.id }),
      }));
  });

  async initialize(): Promise<void> {
    if (this._initialized) {
      return;
    }
    this._initialized = true;

    // CRITICAL: Reset sidebar disabled state on initialization
    // This prevents sidebar from being stuck in disabled state after auto-logout
    // (e.g., if user was logged out while a dashboard modal was open)
    this.setSidebarDisabled(false);

    const userPromise = firstValueFrom(this.authService.user$.pipe(filter(Boolean)));
    await Promise.all([this.initSystemSettings(), this.loadProjects()]);

    const projects = this.projects();
    const user = await userPromise;

    if (!projects.length) {
      return;
    }

    if (user!.isSuperuser) {
      await this.loadCompanies();
    }

    let selectedProjectId = localStorage.getItem(LocalStorageKeys.SELECTED_PROJECT_ID) ?? '';
    const loginProjectName = localStorage.getItem(LocalStorageKeys.LOGIN_PROJECT_NAME) ?? null;
    let validProject = false;

    projects.map(it => {
      if (selectedProjectId === it.id) {
        validProject = true;
      }
      if (loginProjectName === it.name) {
        selectedProjectId = it.id;
        localStorage.setItem(LocalStorageKeys.SELECTED_PROJECT_ID, it.id);
        localStorage.removeItem(LocalStorageKeys.LOGIN_PROJECT_NAME);
      }
    });

    // validProject = projects.find((p) => p.id === selectedProjectId);

    if (!validProject) {
      selectedProjectId = projects[0].id;
      localStorage.setItem(LocalStorageKeys.SELECTED_PROJECT_ID, selectedProjectId);
    }

    this.selectedCompany = this.getCompanyIdFromProject(selectedProjectId);
    const label = this.getLabelById(this.projects(), selectedProjectId)!;
    await this.authService.validateProjectUserPermissions(label);
    this._selectedProject.set(selectedProjectId);
    this._isInitialized.set(true);
  }

  async loadCompanies(): Promise<void> {
    try {
      const url = `${ENVIRONMENT.API_URL}companies/?page=1&page_size=1000`;
      const response = await fetchWithCsrf<{ data: Company[]; }>(url, {
        method: 'GET',
        credentials: 'include',
      });
      this.companies.set(response?.data ?? []);
      const savedCompany = this.localStorageService.getItem(LocalStorageKeys.SELECTED_COMPANY_ID);
      this._selectedCompany.set(savedCompany || (this.companiesListOptions()[0]?.value as string) || '');
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  }

  async loadProjects(): Promise<void> {
    try {
      const data = {
        is_active: 'true',
        page_size: '400',
      };
      const params = new URLSearchParams(data);
      const url = `${ENVIRONMENT.API_URL}projects/?${params.toString()}`;

      const response = await fetchWithCsrf<{ data: Project[]; }>(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      const projectsData = response?.data ?? [];

      this.projects.set(
        projectsData.map((project) => ({
          ...project,
          id: String(project.id),
        })),
      );

      this.filterProjectsByCompany(this.selectedCompany || '');

      const storedProject = this.localStorageService.getItem(
        LocalStorageKeys.SELECTED_PROJECT_ID,
      );

      const relevantProject = this.projects().find(
        (project) => project.id === storedProject,
      );

      this.selectedProject = relevantProject?.id ?? projectsData[0]?.id ?? '';
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  }

  get selectedCompany(): string {
    return this._selectedCompany();
  }

  set selectedCompany(value: string | null) {
    if (!value) return;

    let finalValue = value;
    const existsById = this.companiesListOptions().some(
      (item) => item.value === value,
    );

    if (!existsById) {
      const companyId = this.companies().find(
        (company) => company.name === value,
      )?.id;
      if (companyId != null) {
        finalValue = String(companyId);
      }
    }

    if (finalValue === this.COMPANY_DEFAULT_SELECTION) {
      this._selectChangeTrigger = true;
      this._selectedCompany.set(finalValue);
      this.localStorageService.setItem(
        LocalStorageKeys.SELECTED_COMPANY_ID,
        String(finalValue),
      );
      return;
    }

    this._selectChangeTrigger = true;
    this._selectedCompany.set(finalValue);
    this.localStorageService.setItem(
      LocalStorageKeys.SELECTED_COMPANY_ID,
      String(finalValue),
    );

    const term = this._companySearchTerm();
    let matchedProjectId = this.findBestProjectMatchInCompany(term, finalValue);

    if (!matchedProjectId) {
      matchedProjectId =
        this.projects()
          .filter(
            (project) => String(project.company?.id) === String(finalValue),
          )
          .sort((projectA, projectB) =>
            projectA.name.localeCompare(projectB.name),
          )[0]?.id ?? null;
    }

    if (matchedProjectId) {
      this.selectedProject = matchedProjectId;
      this._companySearchTerm.set('');
    }
  }

  private findBestProjectMatchInCompany(term: string, companyId: string): string | null {
    if (!term || !companyId) {
      const savedProjectId = localStorage.getItem(LocalStorageKeys.SELECTED_PROJECT_ID);
      if (savedProjectId) {
        const savedProject = this.projects().find(p => p.id === savedProjectId);
        if (savedProject && String(savedProject.company?.id) === String(companyId)) {
          return savedProjectId;
        }
      }
      return null;
    }

    const termTrim = term.toLowerCase().trim();

    const projectsInCompany = this.projects().filter(
      (project) => String(project.company?.id) === String(companyId),
    );

    if (!projectsInCompany.length) {
      return null;
    }

    const exact = projectsInCompany.find(
      (project) => project.name.toLowerCase() === termTrim,
    );
    if (exact) {
      return exact.id;
    }

    const starts = projectsInCompany.find((project) =>
      project.name.toLowerCase().startsWith(termTrim),
    );
    if (starts) {
      return starts.id;
    }

    const includes = projectsInCompany.find((project) =>
      project.name.toLowerCase().includes(termTrim),
    );
    return includes?.id ?? null;
  }

  get selectedProject(): string | null {
    return this._selectedProject();
  }

  set selectedProject(value: string | null) {
    if (!value || this._selectedProject() === value) {
      return;
    }
    this.setSelectedProject(value);
  }

  async setSelectedProject(projectId: string | null) {
    if (projectId == null || this._selectedProject() === projectId) {
      return;
    }

    localStorage.setItem(LocalStorageKeys.SELECTED_PROJECT_ID, projectId);

    this.updateLanguageByProjectDirection();

    const label = this.getLabelById(this.projects(), projectId);
    if (label) {
      await this.authService.validateProjectUserPermissions(label);
      localStorage.setItem(LocalStorageKeys.SELECTED_PROJECT_NAME, label);
    }

    this._selectChangeTrigger = true;

    const exists = this.projectsListOptions().some((item) => item.value === projectId);
    if (!exists) {
      const companyId = this.getCompanyIdFromProject(projectId);
      if (companyId) {
        this._selectedCompany.set(companyId);
      }
    }

    this._selectedProject.set(projectId);
    localStorage.setItem(LocalStorageKeys.SELECTED_PROJECT_ID, projectId);

    // TODO: REMOVE WHILE MIGRATION TO ANGULAR DONE
    const managerAppKeys = [
      'ngStorage-create_workflow_project',
      'ngStorage-process_project',
      'ngStorage-selectedProjectName',
      'ngStorage-upload_workflow_project',
    ];
    setTimeout(() => {
      managerAppKeys.forEach((key) => {
        localStorage.setItem(key, JSON.stringify(label));
      });
    }, 500);
  }

  getCompanyIdFromProject(projectId: string): string | null {
    if (!projectId) {
      return null;
    }

    return (this.projects().find((project) => project.id === projectId)?.company?.id || null);
  }

  get selectChangeTrigger(): boolean {
    return this._selectChangeTrigger;
  }

  set selectChangeTrigger(value: boolean) {
    this._selectChangeTrigger = value;
  }

  readonly selectedCompany$ = toObservable(this._selectedCompany, { injector: this.injector });

  readonly selectedProject$ = toObservable(this._selectedProject, { injector: this.injector });

  getSelectedProject(): string {
    const projectId = this._selectedProject();
    return this.projectsListOptions().find((option) => option.value === projectId)?.label || '';
  }

  getLabelById(data: (Company | Project)[], id: string): string | null {
    return (data.find((element) => String(element.id) === id)?.name || null);
  }

  getIdByLabel(
    data: (Company | Project)[],
    label: string | null,
  ): string | number | null {
    if (!label) return null;
    return data.find((i) => i.name === label)?.id ?? null;
  }

  initSystemSettings(): Promise<void> {
    return fetch(`${ENVIRONMENT.API_URL}system`)
      .then((response) => response.json())
      .then((data) => (this.systemSettings = data));
  }

  public handleHttpError(error: HttpErrorResponse): Observable<never> {
    console.warn('Handling HTTP error', error);
    // Only redirect to login on 401 (Unauthorized) - user session expired or not authenticated
    // Do NOT redirect on 403 (Forbidden) - user is authenticated but lacks permission
    // 403 errors should be handled gracefully by the component (show error message, disable features, etc.)
    if (error.status === 401) {
      this.router.navigate(['/login']);
    }
    return throwError(() => error);
  }

  public async createSupportCallForm(transaction = ''): Promise<string | null> {
    const csrfToken = getCookie('csrftoken') || '';
    const body = {
      project: this.getSelectedProject(),
      email: this.authService.user?.email || '',
      env: ENVIRONMENT.SENTRY_ENVIRONMENT,
      ...(transaction && { transaction }),
    };
    try {
      const response = await firstValueFrom(
        this.http.post<{ form_url: string; }>(
          `${ENVIRONMENT.API_URL}${ENVIRONMENT.SUPPORT_CALL_ROUTE}`,
          body,
          {
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': csrfToken,
            },
            withCredentials: true,
          },
        ),
      );
      return response.form_url ?? null;
    } catch (e) {
      console.error('Failed to create support form:', e);
      return null;
    }
  }

  filterProjectsByCompany(companyId: string) {
    return this.projects()
      .filter(
        (unit) =>
          companyId === this.COMPANY_DEFAULT_SELECTION ||
          companyId === unit.company?.id,
      )
      .map((unit) => ({
        value: unit.id,
        label: unit.name,
        ...(unit?.company?.id && { data: unit.company.id }),
      }));
  }

  private updateLanguageByProjectDirection(): void {
    const projectId = this._selectedProject();
    const direction = this.projects().find(
      (project) => project.id === projectId,
    )?.direction;
    const languageCode = direction === LanguageDirection.HE ? Languages.HE : Languages.EN;

    // Save the project's language to localStorage so it persists after logout
    this.localStorageService.setItem('selectedLanguage', languageCode);

    this.translateService.use(languageCode);
  }

  direction() {
    return this.projects().find((project) => project.id === this._selectedProject())?.direction;
  }
}
